import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TriangleAlert } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { SettingsField, SettingsToggleRow } from '../../../../../../components/design-system/common/SettingsPanel';
import { PriceText } from '../../../../../../components/design-system/common/PriceText';
import { useFormatPrice } from '../../../../../../common/hooks/use-format-price.hook';
import {
  DEFAULT_PRICE_CURRENCY,
  PRICE_CURRENCIES,
  PRICE_UNITS,
  normalizeCurrency,
  parseDecimal,
  priceSourceLabel,
  priceUnitLabel,
  defaultPriceUnit,
  type PriceSource,
  type PriceUnit,
} from '../../../../../../common/utils/product-pricing';
import type { ProductCommerce, ProductPack, ProductSample } from '../../../../../../common/models/product-v2.model';
import type { ProductFormStateV3, VariantV3 } from '../../../types';
import {
  describePack,
  describePrice,
  loadedCommerceOf,
  manualPack,
  manualPrice,
  packToRestore,
  priceToRestore,
  variantLabel,
  variantsDiffer,
  type CommerceMoney,
} from '../../../utils/pricePackaging';
import { Card } from './Card';

export interface CommerceChange {
  uuid: string;
  commerce: ProductCommerce;
}

interface PricePackagingCardV3Props {
  /** The product form state (the default variant's document). */
  product: ProductFormStateV3;
  variants: VariantV3[];
  /** Surfaces are sold by the m² unless the vendor says otherwise. */
  isSurface: boolean;
  /**
   * Write new commerce onto variants, and onto the product so variants
   * generated later start from it.
   */
  onApply: (changes: CommerceChange[], productCommerce?: ProductCommerce) => void;
}

type Translate = (key: string, defaultValue?: string) => string;

const USE_PRICE_FALLBACK: Record<PriceSource, string> = {
  manual: 'Use my price',
  shop: 'Use shop price',
  page: 'Use website price',
  import: 'Use imported price',
  attributes: 'Use price from product details',
};
const USE_PACK_FALLBACK: Record<PriceSource, string> = {
  manual: 'Use my packaging',
  shop: 'Use shop packaging',
  page: 'Use website packaging',
  import: 'Use imported packaging',
  attributes: 'Use packaging from product details',
};

/**
 * A number field that lets people type the way they write numbers ("24,90"),
 * and reports a number — or undefined when emptied. Zero only where it means
 * something (a free sample); a zero price is no price.
 */
const DecimalInput: React.FC<{
  id?: string;
  value: number | undefined;
  onValueChange: (value: number | undefined) => void;
  placeholder?: string;
  allowZero?: boolean;
  integer?: boolean;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}> = ({ id, value, onValueChange, placeholder, allowZero, integer, className, ...rest }) => {
  const [text, setText] = useState(value === undefined ? '' : String(value));
  const [invalid, setInvalid] = useState(false);

  // Follow changes made elsewhere (take over, go back, reset) without fighting the typing.
  useEffect(() => {
    const typed = text.trim() === '' ? undefined : parseDecimal(text);
    if (typed !== value) {
      setText(value === undefined ? '' : String(value));
      setInvalid(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (raw: string) => {
    setText(raw);
    if (raw.trim() === '') {
      setInvalid(false);
      onValueChange(undefined);
      return;
    }
    const parsed = parseDecimal(raw);
    const ok = parsed !== undefined && (allowZero ? parsed >= 0 : parsed > 0) && (!integer || Number.isInteger(parsed));
    setInvalid(!ok);
    if (ok) onValueChange(parsed);
    else if (parsed === 0) onValueChange(undefined);
  };

  return (
    <Input
      id={id}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      value={text}
      placeholder={placeholder}
      aria-invalid={invalid || undefined}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={() => {
        if (invalid) {
          setText(value === undefined ? '' : String(value));
          setInvalid(false);
        }
      }}
      className={cn('tabular-nums', className)}
      {...rest}
    />
  );
};

/** A quiet badge: where a value came from. */
const SourceBadge: React.FC<{ t: Translate; source: PriceSource | null; testId?: string }> = ({ t, source, testId }) =>
  source ? (
    <Badge variant="outline" className="text-muted-foreground" data-testid={testId}>
      {priceSourceLabel(t, source)}
    </Badge>
  ) : null;

/** "Unit not stated — check it": nobody said what the price is per. */
const UnitAssumedNote: React.FC<{ t: Translate }> = ({ t }) => (
  <p className="flex items-start gap-1.5 text-xs text-warning" data-testid="price-unit-assumed">
    <TriangleAlert className="mt-px size-3.5 shrink-0" />
    <span>
      {t('PRODUCT_PRICING.CARD.UNIT_ASSUMED', 'Unit not stated — check it')}
      <span className="text-muted-foreground">
        {' · '}
        {t('PRODUCT_PRICING.CARD.UNIT_ASSUMED_HINT', 'Nobody said what this price is per, so square metres are assumed.')}
      </span>
    </span>
  </p>
);

/** A section heading inside the card. */
const SectionTitle: React.FC<{ children: React.ReactNode; action?: React.ReactNode }> = ({ children, action }) => (
  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
    <div className="flex flex-wrap items-center gap-2">{children}</div>
    {action}
  </div>
);

/**
 * Price & packaging — what each variant sells for, how it is boxed, and
 * whether a sample can be ordered. Shown for every family.
 *
 * A price the vendor typed is theirs and editable. A price that only comes
 * from their shop, their product page, an import or the product details is
 * shown read-only with where it came from, and "Set my own price" takes it
 * over. Variants share one set of fields; when they differ, a compact table
 * sets each one.
 */
export const PricePackagingCardV3: React.FC<PricePackagingCardV3Props> = ({ product, variants, isSurface, onApply }) => {
  const { t } = useTranslation();
  const tr = t as unknown as Translate;
  const format = useFormatPrice();
  const fieldId = useId();

  const selected = variants.filter((v) => v.isSelected);
  // The variant whose values the shared fields show; before any variant exists, the product itself.
  const reference: VariantV3 = selected.find((v) => v.isDefault) ?? selected[0] ?? ({
    uuid: '',
    axes: {},
    isSelected: true,
    isDefault: true,
    visualization: product?.visualization,
    commerce: product?.commerce ?? {},
    pricing: product?.pricing,
  } as VariantV3);
  const refCommerce: ProductCommerce | undefined = reference.commerce;
  const refPricing = reference.pricing;
  const differ = variantsDiffer(variants);

  // Null until the vendor picks: per-variant follows whether the variants differ.
  const [perVariantChoice, setPerVariantChoice] = useState<boolean | null>(null);
  const perVariant = selected.length > 1 && (perVariantChoice ?? differ);
  const showShared = !differ || perVariant;

  const priceView = describePrice(refCommerce?.price, refPricing?.price);
  const packView = describePack(refCommerce?.pack, refPricing?.pack);
  const manual = priceView.state === 'manual' && showShared ? refCommerce!.price! : null;

  // The pickers' values before any price is typed: the resolved price's, else a starting default.
  const [draftCurrency, setDraftCurrency] = useState<string | null>(null);
  const [draftUnit, setDraftUnit] = useState<PriceUnit | null>(null);
  const currency = manual?.currency ?? draftCurrency ?? priceView.price?.currency ?? DEFAULT_PRICE_CURRENCY;
  const unit: PriceUnit | undefined = manual
    ? manual.unit
    : draftUnit ?? priceView.price?.unit ?? defaultPriceUnit(isSurface);
  const currencies = Array.from(new Set<string>([...PRICE_CURRENCIES, currency]));

  // ── Writing ──────────────────────────────────────────────────────────────

  /** One change for every variant (kept on unselected ones too, so a restored row agrees). */
  const applyAll = (patch: CommerceMoney | ((variant: VariantV3) => CommerceMoney)) => {
    const patchFor = (v: VariantV3) => (typeof patch === 'function' ? patch(v) : patch);
    const changes = variants.map((v) => ({ uuid: v.uuid, commerce: { ...(v.commerce || {}), ...patchFor(v) } }));
    const productPatch = patchFor(reference);
    onApply(changes, { ...(product?.commerce || {}), ...productPatch });
  };

  /** One variant's change; the product follows its default variant. */
  const applyOne = (variant: VariantV3, patch: CommerceMoney) => {
    onApply(
      [{ uuid: variant.uuid, commerce: { ...(variant.commerce || {}), ...patch } }],
      variant === reference ? { ...(product?.commerce || {}), ...patch } : undefined,
    );
  };

  const restorePrice = (v: VariantV3): CommerceMoney => ({ price: priceToRestore(loadedCommerceOf(v)) });
  const restorePack = (v: VariantV3): CommerceMoney => ({ pack: packToRestore(loadedCommerceOf(v)) });

  const setAmount = (amount: number | undefined) => {
    if (!amount) return applyAll(restorePrice);
    applyAll({ price: manualPrice({ amount, currency, unit, compareAt: manual?.compareAt }) });
  };

  const setCompareAt = (compareAt: number | undefined) => {
    if (!manual) return;
    applyAll({ price: manualPrice({ ...manual, compareAt }) });
  };

  /** Currency and unit: on every variant priced by the vendor, and for prices typed next. */
  const setCurrencyOrUnit = (change: { currency?: string; unit?: PriceUnit }) => {
    if (change.currency) setDraftCurrency(change.currency);
    if (change.unit) setDraftUnit(change.unit);
    const patchFor = (v: VariantV3): CommerceMoney => {
      const own = v.commerce?.price;
      const patch: CommerceMoney = {};
      if (own && describePrice(own, v.pricing?.price).state === 'manual') {
        patch.price = manualPrice({ ...own, ...change });
      }
      // A sample price is in the product's currency.
      if (change.currency && v.commerce?.sample && typeof v.commerce.sample.price === 'number') {
        patch.sample = { ...v.commerce.sample, currency: change.currency };
      }
      return patch;
    };
    // Nothing priced yet: the pickers only set what the next typed price uses.
    if (![reference, ...variants].some((v) => Object.keys(patchFor(v)).length > 0)) return;
    applyAll(patchFor);
  };

  const takeOverPrice = () => {
    const shown = priceView.price;
    if (!shown) return;
    applyAll({ price: manualPrice({ ...shown, unit: shown.unit ?? unit }) });
  };

  const setPackField = (field: 'm2PerBox' | 'piecesPerBox', value: number | undefined) => {
    const current = packView.state === 'manual' && showShared ? packView.pack ?? {} : {};
    const pack = manualPack({ ...current, [field]: value });
    if (!pack) return applyAll(restorePack);
    applyAll({ pack });
  };

  const takeOverPack = () => {
    if (packView.pack) applyAll({ pack: manualPack(packView.pack) });
  };

  // Per-variant rows ------------------------------------------------------

  const setVariantAmount = (v: VariantV3, amount: number | undefined) => {
    setPerVariantChoice(true);
    if (!amount) return applyOne(v, restorePrice(v));
    const own = v.commerce?.price;
    const ownManual = describePrice(own, v.pricing?.price).state === 'manual' ? own : null;
    applyOne(v, {
      price: manualPrice({ amount, currency, unit: ownManual?.unit ?? unit, compareAt: ownManual?.compareAt }),
    });
  };

  const setVariantPack = (v: VariantV3, field: 'm2PerBox' | 'piecesPerBox', value: number | undefined) => {
    setPerVariantChoice(true);
    // Typing over a shop or page pack keeps its other half rather than dropping it.
    const view = describePack(v.commerce?.pack, v.pricing?.pack);
    const pack = manualPack({ ...(view.pack ?? {}), [field]: value });
    applyOne(v, pack ? { pack } : restorePack(v));
  };

  // Samples ---------------------------------------------------------------

  const sample: ProductSample | null | undefined = refCommerce?.sample;
  const samplesOn = sample?.available === true;

  const setSamplesOn = (on: boolean) => {
    if (on) {
      const price = typeof sample?.price === 'number' ? sample.price : undefined;
      return applyAll({ sample: { available: true, ...(price !== undefined ? { price, currency } : {}) } });
    }
    // Off is "no samples" — unless the variant never said anything, which stays unsaid.
    applyAll((v) => {
      const loaded = loadedCommerceOf(v)?.sample;
      return { sample: loaded === undefined || loaded === null ? (loaded as null | undefined) : { available: false } };
    });
  };

  const setSamplePrice = (price: number | undefined) => {
    applyAll({ sample: { available: true, ...(price !== undefined ? { price, currency } : {}) } });
  };

  // ── Rendering ────────────────────────────────────────────────────────────

  const unitSelect = (
    <Select value={unit ?? ''} onValueChange={(value) => setCurrencyOrUnit({ unit: value as PriceUnit })}>
      <SelectTrigger id={`${fieldId}-unit`} className="w-full" data-testid="price-unit">
        <SelectValue placeholder={tr('PRODUCT_PRICING.CARD.UNIT_PLACEHOLDER', 'Choose')} />
      </SelectTrigger>
      <SelectContent>
        {PRICE_UNITS.map((u) => (
          <SelectItem key={u} value={u}>
            {priceUnitLabel(tr, u, 'long')}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const currencySelect = (
    <Select
      value={currency}
      onValueChange={(value) => setCurrencyOrUnit({ currency: normalizeCurrency(value) ?? value })}
    >
      <SelectTrigger id={`${fieldId}-currency`} className="w-full" data-testid="price-currency">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {currencies.map((c) => (
          <SelectItem key={c} value={c}>
            {c}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const priceSection = (() => {
    if (perVariant) {
      return (
        <>
          <SectionTitle>
            <p className="text-sm font-medium text-foreground">{tr('PRODUCT_PRICING.CARD.PRICE', 'Price')}</p>
          </SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <SettingsField label={tr('PRODUCT_PRICING.CARD.CURRENCY', 'Currency')} htmlFor={`${fieldId}-currency`}>
              {currencySelect}
            </SettingsField>
            <SettingsField label={tr('PRODUCT_PRICING.CARD.UNIT', 'Per')} htmlFor={`${fieldId}-unit`}>
              {unitSelect}
            </SettingsField>
          </div>
        </>
      );
    }

    if (showShared && priceView.state === 'auto' && priceView.price) {
      return (
        <>
          <SectionTitle
            action={
              <Button variant="outline" size="sm" onClick={takeOverPrice} data-testid="price-take-over">
                {tr('PRODUCT_PRICING.CARD.SET_OWN_PRICE', 'Set my own price')}
              </Button>
            }
          >
            <p className="text-sm font-medium text-foreground">{tr('PRODUCT_PRICING.CARD.PRICE', 'Price')}</p>
            <SourceBadge t={tr} source={priceView.source} testId="price-source" />
          </SectionTitle>
          <PriceText price={priceView.price} className="text-lg font-medium text-foreground" data-testid="price-resolved" />
          {priceView.unitAssumed && (
            <div className="mt-2">
              <UnitAssumedNote t={tr} />
            </div>
          )}
        </>
      );
    }

    const fallback = manual ? priceView.fallbackSource : null;
    return (
      <>
        <SectionTitle
          action={
            fallback ? (
              <Button variant="outline" size="sm" onClick={() => applyAll(restorePrice)} data-testid="price-use-source">
                {tr(`PRODUCT_PRICING.USE_PRICE.${fallback.toUpperCase()}`, USE_PRICE_FALLBACK[fallback])}
              </Button>
            ) : undefined
          }
        >
          <p className="text-sm font-medium text-foreground">{tr('PRODUCT_PRICING.CARD.PRICE', 'Price')}</p>
          {manual && <SourceBadge t={tr} source="manual" testId="price-source" />}
        </SectionTitle>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SettingsField label={tr('PRODUCT_PRICING.CARD.AMOUNT', 'Price')} htmlFor={`${fieldId}-amount`}>
            <DecimalInput
              id={`${fieldId}-amount`}
              value={manual?.amount}
              onValueChange={setAmount}
              placeholder={differ ? tr('PRODUCT_PRICING.CARD.VARIES', 'Varies') : '0.00'}
              data-testid="price-amount"
            />
          </SettingsField>
          <SettingsField label={tr('PRODUCT_PRICING.CARD.CURRENCY', 'Currency')} htmlFor={`${fieldId}-currency`}>
            {currencySelect}
          </SettingsField>
          <SettingsField label={tr('PRODUCT_PRICING.CARD.UNIT', 'Per')} htmlFor={`${fieldId}-unit`}>
            {unitSelect}
          </SettingsField>
          <SettingsField label={tr('PRODUCT_PRICING.CARD.COMPARE_AT', 'Compare-at price')} htmlFor={`${fieldId}-compare`}>
            <DecimalInput
              id={`${fieldId}-compare`}
              value={manual?.compareAt}
              onValueChange={setCompareAt}
              placeholder={tr('PRODUCT_PRICING.CARD.OPTIONAL', 'Optional')}
              data-testid="price-compare-at"
            />
          </SettingsField>
        </div>
        {manual && !manual.unit && (
          <div className="mt-2">
            <UnitAssumedNote t={tr} />
          </div>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          {differ
            ? tr('PRODUCT_PRICING.CARD.VARIANTS_DIFFER', 'Your variants have different prices or packaging. What you enter here applies to all of them.')
            : !manual
              ? tr('PRODUCT_PRICING.CARD.NO_PRICE', 'No price yet. Shoppers see how much they need, but no total.')
              : tr('PRODUCT_PRICING.CARD.COMPARE_AT_HINT', 'The compare-at price is the price before a discount, shown struck through.')}
        </p>
      </>
    );
  })();

  const variantTable = perVariant ? (
    <div className="mt-4 overflow-hidden rounded-lg border" data-testid="price-variant-table">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-start text-muted-foreground">{tr('PRODUCT_PRICING.CARD.VARIANT', 'Variant')}</TableHead>
            <TableHead className="w-28 text-start text-muted-foreground">{tr('PRODUCT_PRICING.CARD.AMOUNT', 'Price')}</TableHead>
            <TableHead className="w-24 text-start text-muted-foreground">{tr('PRODUCT_PRICING.CARD.M2_PER_BOX', 'm² per box')}</TableHead>
            <TableHead className="w-24 text-start text-muted-foreground">{tr('PRODUCT_PRICING.CARD.PIECES_PER_BOX', 'Pieces per box')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {selected.map((v, index) => {
            const view = describePrice(v.commerce?.price, v.pricing?.price);
            const pack = describePack(v.commerce?.pack, v.pricing?.pack);
            const label = variantLabel(v, index);
            return (
              <TableRow key={v.uuid} data-testid="price-variant-row">
                <TableCell className="whitespace-normal">
                  <p className="text-sm text-foreground">{label}</p>
                  {view.state === 'auto' && view.price && (
                    <p className="text-[11px] text-muted-foreground">
                      {priceSourceLabel(tr, view.source!)} · {format.price(view.price)}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <DecimalInput
                    value={view.state === 'manual' ? view.price!.amount : undefined}
                    onValueChange={(amount) => setVariantAmount(v, amount)}
                    placeholder={view.state === 'auto' && view.price ? String(view.price.amount) : '—'}
                    aria-label={`${tr('PRODUCT_PRICING.CARD.AMOUNT', 'Price')} — ${label}`}
                    className="h-7"
                  />
                </TableCell>
                <TableCell>
                  <DecimalInput
                    value={pack.state === 'manual' ? pack.pack?.m2PerBox : undefined}
                    onValueChange={(value) => setVariantPack(v, 'm2PerBox', value)}
                    placeholder={pack.state === 'auto' && pack.pack?.m2PerBox ? String(pack.pack.m2PerBox) : '—'}
                    aria-label={`${tr('PRODUCT_PRICING.CARD.M2_PER_BOX', 'm² per box')} — ${label}`}
                    className="h-7"
                  />
                </TableCell>
                <TableCell>
                  <DecimalInput
                    integer
                    value={pack.state === 'manual' ? pack.pack?.piecesPerBox : undefined}
                    onValueChange={(value) => setVariantPack(v, 'piecesPerBox', value)}
                    placeholder={pack.state === 'auto' && pack.pack?.piecesPerBox ? String(pack.pack.piecesPerBox) : '—'}
                    aria-label={`${tr('PRODUCT_PRICING.CARD.PIECES_PER_BOX', 'Pieces per box')} — ${label}`}
                    className="h-7"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  ) : null;

  const packSection = perVariant ? null : (() => {
    if (showShared && packView.state === 'auto' && packView.pack) {
      const parts = [
        packView.pack.m2PerBox
          ? String(t('PRODUCT_PRICING.CARD.PACK_M2', { value: packView.pack.m2PerBox, defaultValue: '{{value}} m² per box' }))
          : null,
        packView.pack.piecesPerBox
          ? String(t('PRODUCT_PRICING.CARD.PACK_PIECES', { value: packView.pack.piecesPerBox, defaultValue: '{{value}} pieces per box' }))
          : null,
      ].filter(Boolean);
      return (
        <>
          <SectionTitle
            action={
              <Button variant="outline" size="sm" onClick={takeOverPack} data-testid="pack-take-over">
                {tr('PRODUCT_PRICING.CARD.SET_OWN_PACK', 'Set my own packaging')}
              </Button>
            }
          >
            <p className="text-sm font-medium text-foreground">{tr('PRODUCT_PRICING.CARD.PACKAGING', 'Packaging')}</p>
            <SourceBadge t={tr} source={packView.source} testId="pack-source" />
          </SectionTitle>
          <p className="text-sm text-foreground tabular-nums" data-testid="pack-resolved">{parts.join(' · ')}</p>
        </>
      );
    }

    const ownPack: ProductPack | null = packView.state === 'manual' && showShared ? packView.pack : null;
    const fallback = ownPack ? packView.fallbackSource : null;
    return (
      <>
        <SectionTitle
          action={
            fallback ? (
              <Button variant="outline" size="sm" onClick={() => applyAll(restorePack)} data-testid="pack-use-source">
                {tr(`PRODUCT_PRICING.USE_PACK.${fallback.toUpperCase()}`, USE_PACK_FALLBACK[fallback])}
              </Button>
            ) : undefined
          }
        >
          <p className="text-sm font-medium text-foreground">{tr('PRODUCT_PRICING.CARD.PACKAGING', 'Packaging')}</p>
          {ownPack && <SourceBadge t={tr} source="manual" testId="pack-source" />}
        </SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <SettingsField label={tr('PRODUCT_PRICING.CARD.M2_PER_BOX', 'm² per box')} htmlFor={`${fieldId}-m2`}>
            <DecimalInput
              id={`${fieldId}-m2`}
              value={ownPack?.m2PerBox}
              onValueChange={(value) => setPackField('m2PerBox', value)}
              placeholder={differ ? tr('PRODUCT_PRICING.CARD.VARIES', 'Varies') : '1.44'}
              data-testid="pack-m2-per-box"
            />
          </SettingsField>
          <SettingsField label={tr('PRODUCT_PRICING.CARD.PIECES_PER_BOX', 'Pieces per box')} htmlFor={`${fieldId}-pieces`}>
            <DecimalInput
              id={`${fieldId}-pieces`}
              integer
              value={ownPack?.piecesPerBox}
              onValueChange={(value) => setPackField('piecesPerBox', value)}
              placeholder={differ ? tr('PRODUCT_PRICING.CARD.VARIES', 'Varies') : '8'}
              data-testid="pack-pieces-per-box"
            />
          </SettingsField>
        </div>
        {!ownPack && !differ && (
          <p className="mt-2 text-xs text-muted-foreground">
            {tr('PRODUCT_PRICING.CARD.NO_PACK', 'Say what one box covers and shoppers are told how many boxes to buy.')}
          </p>
        )}
      </>
    );
  })();

  return (
    <Card
      title={tr('PRODUCT_PRICING.CARD.TITLE', 'Price & packaging')}
      testId="price-packaging-card"
      action={
        selected.length > 1 ? (
          <Button
            variant="ghost"
            size="xs"
            className="text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              setPerVariantChoice(!perVariant);
            }}
            data-testid="price-per-variant-toggle"
          >
            {perVariant
              ? tr('PRODUCT_PRICING.CARD.SAME_FOR_ALL', 'Same for every variant')
              : tr('PRODUCT_PRICING.CARD.PER_VARIANT', 'Set per variant')}
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col">
        <div>{priceSection}</div>
        {variantTable}
        {packSection && <div className="mt-5 border-t pt-5">{packSection}</div>}
        <div className="mt-5 border-t pt-5">
          <SettingsToggleRow
            id={`${fieldId}-samples`}
            label={tr('PRODUCT_PRICING.CARD.SAMPLES', 'Samples available')}
            description={tr('PRODUCT_PRICING.CARD.SAMPLES_DESC', 'Shoppers can ask for a sample of this product.')}
            checked={samplesOn}
            onCheckedChange={setSamplesOn}
          >
            <SettingsField
              label={tr('PRODUCT_PRICING.CARD.SAMPLE_PRICE', 'Sample price')}
              htmlFor={`${fieldId}-sample-price`}
              hint={tr('PRODUCT_PRICING.CARD.SAMPLE_PRICE_HINT', '0 means free. Leave it empty if you have not decided.')}
            >
              <div className="flex items-center gap-2">
                <DecimalInput
                  id={`${fieldId}-sample-price`}
                  allowZero
                  value={typeof sample?.price === 'number' ? sample.price : undefined}
                  onValueChange={setSamplePrice}
                  placeholder={currency}
                  className="max-w-40"
                  data-testid="sample-price"
                />
                {sample?.price === 0 && (
                  <Badge variant="outline" className="text-muted-foreground" data-testid="sample-free">
                    {tr('PRODUCT_PRICING.CARD.FREE', 'Free')}
                  </Badge>
                )}
              </div>
            </SettingsField>
          </SettingsToggleRow>
        </div>
      </div>
    </Card>
  );
};
